import { defineComponent as _defineComponent } from 'vue';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import QueryWithSearchDropdown from '@/components/QueryWithSearchDropdown.vue';
import axios from 'axios';
import { formatNumber, isNumeric, ucfirst } from '@/utils/utils';
import SmartComponent from '@/components/SmartComponent.vue';
import AlloyDialog from '@/components/AlloyDialog.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LoadingError from '@/components/LoadingError.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AlloysContent',
    setup(__props, { expose: __expose }) {
        __expose();
        const { t: $t, locale } = useI18n();
        const route = useRoute();
        const currentDateOption = new Date().toISOString().slice(0, 7);
        const validDateOptions = generateDateArray(2000).reverse();
        const dateOptions = validDateOptions.map((date) => ({
            key: date,
            label: date,
        }));
        function generateDateArray(startYear) {
            const dateArray = [];
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1;
            yearLoop: for (let year = startYear; year <= currentYear; year++) {
                for (let month = 1; month <= 12; month++) {
                    if (year === currentYear && month > currentMonth) {
                        break yearLoop;
                    }
                    const formattedMonth = month.toString().padStart(2, '0');
                    dateArray.push(`${year}-${formattedMonth}`);
                }
            }
            return dateArray;
        }
        const currentDate = ref(currentDateOption);
        const currentLanguage = ref(locale.value);
        const isFirstTrigger = ref(true);
        const isLoading = ref(false);
        const error = ref(null);
        const alloys = ref([]);
        watch(() => [route.query, locale], async () => {
            const newDate = route.query.date;
            const newLanguage = locale.value;
            if (isFirstTrigger.value ||
                newDate !== currentDate.value ||
                newLanguage !== currentLanguage.value) {
                isFirstTrigger.value = false;
                currentDate.value = validDateOptions.includes(newDate) ? newDate : currentDateOption;
                currentLanguage.value = newLanguage;
                isLoading.value = true;
                error.value = null;
                alloys.value = [];
                try {
                    const response = await axios.get(`${settings.siteAddress}/app/api/alloy-surcharges.php`, {
                        params: {
                            month: currentDate.value,
                            language: settings.languageCode.charAt(0).toUpperCase(),
                        },
                    });
                    const newAlloys = [];
                    for (const alloy of Object.values(response.data.data)) {
                        if (!alloy?.tables?.length) {
                            continue;
                        }
                        const newAlloy = {
                            sectionTitle: alloy.sectionTitle,
                            tables: [],
                        };
                        for (const table of alloy.tables) {
                            const newTable = {
                                caption: null,
                                head: [],
                                body: {
                                    rows: [],
                                },
                            };
                            if (table.caption) {
                                if (table.captionHistoryIndex) {
                                    newTable.caption = {
                                        is: 'a',
                                        slot: ucfirst(table.caption),
                                        click: (event) => {
                                            event.preventDefault();
                                            openDialog({
                                                alloy: table.captionHistoryIndex,
                                                to: currentDate.value,
                                                prefix: ucfirst(table.caption) || 'W-No.',
                                                language: currentLanguage.value === 'en' ? 'E' : 'D',
                                            });
                                        },
                                    };
                                }
                                else {
                                    newTable.caption = {
                                        is: 'h2',
                                        slot: ucfirst(table.caption),
                                    };
                                }
                            }
                            for (const [index, header] of table.header.entries()) {
                                newTable.head.push(ucfirst(header) + (table.unit[index]?.trim() ? ` ${table.unit[index]}` : ''));
                            }
                            for (const row of table.rows) {
                                let index = 0;
                                const historyIndexes = table.rowHistoryIndex || [];
                                const newTableRow = [];
                                if (!Array.isArray(row)) {
                                    continue;
                                }
                                for (const cell of row) {
                                    let value;
                                    if (isNumeric(cell) && typeof cell !== 'string') {
                                        value = formatNumber(cell);
                                    }
                                    else if (cell === null) {
                                        value = '';
                                    }
                                    else {
                                        if (index === 0) {
                                            if (historyIndexes.length && historyIndexes[index]) {
                                                value = {
                                                    is: 'a',
                                                    slot: ucfirst(cell),
                                                    click: (event) => {
                                                        event.preventDefault();
                                                        openDialog({
                                                            alloy: cell.toUpperCase().replaceAll(' ', ''),
                                                            to: currentDate.value,
                                                            prefix: ucfirst(table.caption) || 'W-No.',
                                                            language: currentLanguage.value === 'en' ? 'E' : 'D',
                                                        });
                                                    },
                                                };
                                            }
                                            else {
                                                value = ucfirst(cell);
                                            }
                                        }
                                        else {
                                            value = ucfirst(cell);
                                        }
                                    }
                                    newTableRow.push(value);
                                    index++;
                                }
                                newTable.body.rows.push(newTableRow);
                            }
                            newAlloy.tables.push(newTable);
                        }
                        newAlloys.push(newAlloy);
                    }
                    alloys.value = newAlloys;
                }
                catch (e) {
                    console.error(e);
                    error.value = $t('Could not load alloy surcharges. Please try again.');
                }
                finally {
                    isLoading.value = false;
                }
            }
        }, {
            immediate: true,
        });
        const isOpen = ref(false);
        const alloyQuery = ref(null);
        function openDialog(query) {
            alloyQuery.value = query;
            isOpen.value = true;
        }
        function closeDialog() {
            isOpen.value = false;
        }
        const __returned__ = { $t, locale, route, currentDateOption, validDateOptions, dateOptions, generateDateArray, currentDate, currentLanguage, isFirstTrigger, isLoading, error, alloys, isOpen, alloyQuery, openDialog, closeDialog, QueryWithSearchDropdown, SmartComponent, AlloyDialog, LoadingSpinner, LoadingError };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
